<script setup>
const props = defineProps({
  context: Object,
})

const { locale, t } = useI18n()

const id = useId()

const formKitInput = computed({
  get: () => props.context._value,
  set: (value) => {
    props.context.node.input(value)
  },
})

const autoCompleteInput = ref(null)

const searchTerm = ref('')
const searchTermThrottled = throttledRef(searchTerm, 500)

const { data, pending } = useFetch(`/server/address_autocomplete`, {
  query: {
    query: searchTermThrottled,
    lang: useI18n().locale.value,
  },
  key: `address_autocomplete-${id}`,
})

const options = computed(() => {
  return data.value?.predictions.map((option) => {
    return {
      label: option.description,
      id: option.place_id,
      terms: option.terms,
      types: option.types,
      structured_formatting: {
        main_text: option.structured_formatting.main_text,
        secondary_text: option.structured_formatting.secondary_text,
      },
    }
  })
})

function handleBlur(e) {
  props.context.handlers.blur(e)
}

watch(autoCompleteInput, async (value, prevValue) => {
  if (!value) {
    formKitInput.value = null
    return
  }

  if (prevValue !== null && prevValue?.id === value.id) {
    return
  }

  const { result } = await $fetch(`/server/address_details`, {
    query: {
      id: value?.id,
      lang: locale.value,
    },
  })
  formKitInput.value = {
    id: result.result.place_id,
    label: result.result.formatted_address,
    address_components: result.result.address_components,
  }
}, { deep: true })
</script>

<template>
  <div @keypress.enter.prevent @focusout="handleBlur">
    <RadixComboboxRoot
      v-model:search-term="searchTerm"
      v-model="autoCompleteInput" class="relative"
      :filter-function="undefined"
      :display-value="(option) => option.label"
    >
      <RadixComboboxAnchor
        class="signature-page:bg-signature-white/10 mt-.4rem w-full inline-flex items-center justify-between gap-[5px] border-px border-transparent bg-neutral-white/10 outline-none transition-border-color transition-shadow focus-within:(border-starline-primary shadow-starline outline-none signature-page:border-signature signature-page:shadow-signature) block-light:bg-neutral-black/10 signature-page:block-light:!bg-signature-black"
      >
        <RadixComboboxInput :id="props.context.id" :name="props.context.node.name" class="w-full p-1.2rem outline-none !bg-transparent block-light:text-white block-light:placeholder-accent-black/20" :placeholder="props.context.placeholder" />
        <button v-if="autoCompleteInput" aria-label="clear selection" class="h-4.4rem px-1rem" @click="autoCompleteInput = null">
          <div class="i-radix-icons-cross-1 size-1.6rem text-neutral-white/40 block-light:text-neutral-black/20 signature-page:block-light:text-neutral-white/40" />
        </button>
        <RadixComboboxTrigger class="h-4.4rem">
          <div class="grid h-full place-items-center px-1rem">
            <div class="i-radix-icons-arrow-down size-1.6rem text-neutral-white/40 block-light:text-neutral-black/20 signature-page:block-light:text-neutral-white/40" />
          </div>
        </RadixComboboxTrigger>
      </RadixComboboxAnchor>

      <RadixComboboxContent class="absolute z-10 mt-2 w-full overflow-hidden border border-neutral-darkGray bg-starline-secondary block-light:border-starline-primary block-light:bg-neutral-white signature-page:!border-signature signature-page:!bg-signature-black">
        <RadixComboboxViewport class="p-.5rem">
          <div v-if="!options.length" class="py-2 text-center text-1.3rem opacity-70">
            <span v-if="pending">{{ t('form.location.loading') }}</span>
            <span v-else-if="!searchTerm">{{ t('form.location.start_typing') }}</span>
            <span v-else>{{ t('form.location.no_results') }}</span>
          </div>

          <RadixComboboxItem
            v-for="(option) in options" :key="option.id"
            class="relative h-4rem flex select-none items-center pl-2.5rem pr-3.5rem text-1.3rem leading-none ring ring-0 ring-starline-primary data-[disabled]:(pointer-events-none text-opacity-50) data-[highlighted]:(shadow-starline outline-none ring-1 signature-page:shadow-signature) signature-page:ring-signature"
            :value="option"
          >
            <RadixComboboxItemIndicator
              class="absolute left-0 w-[25px] inline-flex items-center justify-center"
            >
              <div class="i-radix-icons-check" />
            </RadixComboboxItemIndicator>
            <div>
              <div>{{ option.structured_formatting.main_text }}</div>
              <div class="text-1.2rem opacity-80">
                {{ option.structured_formatting.secondary_text }}
              </div>
            </div>
          </RadixComboboxItem>
        </RadixComboboxViewport>
      </RadixComboboxContent>
    </RadixComboboxRoot>
  </div>
</template>
